import isAccessTokenStillValid from '@/modules/auth/functions/isAccessTokenStillValid';
import isRefreshTokenStillValid from '@/modules/auth/functions/isRefreshTokenStillValid';
import useLogoutUser from '@/modules/auth/hooks/useLogoutUser';
import useRefreshTokenMutation from '@/modules/auth/mutations/useRefreshTokenMutation';
import useAuthUserStore from '@/modules/auth/stores/useAuthUserStore';
import config from '@/modules/config/functions/config';
import { pick } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

const TOKEN_CHECK_INTERVAL = config(
  'features.auth.tokenExpirationCheckInterval',
  30 * 60 * 1000
);

export default function useAutoRefreshToken() {
  const userState = useAuthUserStore(
    useShallow((state) => pick(state, ['status', 'user']))
  );
  const [isRefreshTokenStillValidState, setIsRefreshTokenStillValidState] =
    useState(true);
  const [isAccessTokenStillValidState, setIsAccessTokenStillValidState] =
    useState(true);
  const [checkedAccessTokenValidity, setCheckedAccessTokenValidity] =
    useState(false);
  const [checkedRefreshTokenValidity, setCheckedRefreshTokenValidity] =
    useState(false);
  const logoutUser = useLogoutUser();
  const refreshTokenMutation = useRefreshTokenMutation();

  // periodically check if the access token is still valid
  useEffect(() => {
    const updateAccessTokenValidity = () => {
      const _isAccessTokenStillValid = isAccessTokenStillValid();
      setIsAccessTokenStillValidState(_isAccessTokenStillValid);
    };

    updateAccessTokenValidity();
    setCheckedAccessTokenValidity(true);
    const interval = setInterval(
      updateAccessTokenValidity,
      TOKEN_CHECK_INTERVAL
    );
    return () => clearInterval(interval);
  }, []);

  // periodically check if the refresh token is still valid
  useEffect(() => {
    if (config('features.auth.enableTokenRefreshing') !== true) return;

    const updateRefreshTokenValidity = () => {
      const _isRefreshTokenStillValid = isRefreshTokenStillValid();
      setIsRefreshTokenStillValidState(_isRefreshTokenStillValid);
    };

    updateRefreshTokenValidity();
    setCheckedRefreshTokenValidity(true);

    const interval = setInterval(
      updateRefreshTokenValidity,
      TOKEN_CHECK_INTERVAL
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (
      userState.status !== 'authenticated' ||
      !checkedAccessTokenValidity ||
      !checkedRefreshTokenValidity ||
      refreshTokenMutation.isPending
    )
      return;

    const currentAccessTokenValid = isAccessTokenStillValid();
    const currentRefreshTokenValid =
      config('features.auth.enableTokenRefreshing') === true
        ? isRefreshTokenStillValid()
        : false;

    if (currentAccessTokenValid) return;

    if (
      config('features.auth.enableTokenRefreshing') === true &&
      currentRefreshTokenValid
    ) {
      refreshTokenMutation.mutate();
    } else {
      logoutUser({ action: 'force_logout', reason: 'session_expired' });
    }
  }, [
    userState.status,
    isAccessTokenStillValidState,
    refreshTokenMutation,
    logoutUser,
    isRefreshTokenStillValidState,
    userState,
    checkedAccessTokenValidity,
    checkedRefreshTokenValidity,
  ]);
}
